import React, { useState } from 'react';
import classnames from 'classnames';

import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import { isMobileUA } from 'dpl/shared/utils';
import MobileMenu from 'dpl/components/MobileMenu';
import Icon from 'dpl/common/components/Icon';

import { PAYMENT_TIER_TYPE, SELECT_PAYMENT_MODAL_NAME } from './constants';
import SelectPaymentType from './SelectPaymentType';
import SelectPaymentTypeLearnMore from './SelectPaymentTypeLearnMore';

const IS_MOBILE_UA = isMobileUA();

type PaymentTierType =
  (typeof PAYMENT_TIER_TYPE)[keyof typeof PAYMENT_TIER_TYPE];
type SelectPaymentModalName =
  (typeof SELECT_PAYMENT_MODAL_NAME)[keyof typeof SELECT_PAYMENT_MODAL_NAME];

interface CurrentModalDisplayProps {
  amount: number;
  fee?: string | null;
  dogPayment: boolean;
  paymentMethod: string | null;
  currentModalName: SelectPaymentModalName;
  onGoBack: (() => void) | false;
  onChangeModal: (newModalName: string, allowGoBack?: boolean) => void;
  onHandleSelection: (type: PaymentTierType) => void;
  onSave: () => void;
  onCloseClick: () => void;
  selectedProtectionType: PaymentTierType;
}

function CurrentModalDisplay({
  amount,
  fee,
  dogPayment,
  paymentMethod,
  currentModalName,
  onGoBack,
  onChangeModal,
  onHandleSelection,
  onSave,
  onCloseClick,
  selectedProtectionType
}: CurrentModalDisplayProps) {
  const learnMoreProps = { onGoBack, dogPayment, onChangeModal, paymentMethod };
  const paymentProtectionProps = {
    onChangeModal,
    selectedProtectionType,
    amount,
    fee,
    dogPayment,
    onHandleSelection,
    onSave,
    onCloseClick
  };

  const mapModalNameToModalComponent: Record<
    SelectPaymentModalName,
    React.ReactElement
  > = {
    [SELECT_PAYMENT_MODAL_NAME.LEARN_MORE]: (
      <SelectPaymentTypeLearnMore {...learnMoreProps} />
    ),
    [SELECT_PAYMENT_MODAL_NAME.PAYMENT_PROTECTION]: (
      <SelectPaymentType {...paymentProtectionProps} />
    )
  };

  return mapModalNameToModalComponent[currentModalName];
}

interface SelectPaymentTypeParentModalProps {
  amount: number;
  dogPayment: boolean;
  paymentTier: PaymentTierType;
  onCloseClick: () => void;
  onSaveSelection: (tier: PaymentTierType) => void;
  defaultModalName?: SelectPaymentModalName | null;
  paymentMethod?: string | null;
  fee?: string | null;
}

export default function SelectPaymentTypeParentModal({
  defaultModalName = SELECT_PAYMENT_MODAL_NAME.PAYMENT_PROTECTION,
  paymentMethod = null,
  ...props
}: SelectPaymentTypeParentModalProps) {
  const [currentModalName, setCurrentModalName] =
    useState<SelectPaymentModalName>(
      defaultModalName ?? SELECT_PAYMENT_MODAL_NAME.PAYMENT_PROTECTION
    );
  const [previousModalName, setPreviousModalName] =
    useState<SelectPaymentModalName | null>(null);
  const [selectedProtectionType, setSelectedProtectionType] =
    useState<PaymentTierType>(props.paymentTier);
  const [isMenuOpen, setIsMenuOpen] = useState(true);

  function handleChangeModal(newModalName: string, allowGoBack = false): void {
    if (allowGoBack) {
      setPreviousModalName(currentModalName);
    } else {
      setPreviousModalName(null);
    }
    setCurrentModalName(newModalName as SelectPaymentModalName);
  }

  function handleGoBack(): void {
    if (previousModalName) {
      setCurrentModalName(previousModalName);
      setPreviousModalName(null);
    }
  }

  function handleSelection(type: PaymentTierType): void {
    setSelectedProtectionType(type);
  }

  function handleSaveSelection(): void {
    props.onSaveSelection(selectedProtectionType);
  }

  if (IS_MOBILE_UA) {
    return (
      <div className="SelectPaymentTypeParentModal">
        <div
          className={classnames(
            'scrim--black fixed z-max left-0 top-0 right-0 bottom-0 transition pointer-events-none',
            {
              'o-0': !isMenuOpen,
              'o-100': isMenuOpen
            }
          )}
        />
        <MobileMenu
          className="no-scrollbars pt6"
          position="bottom"
          isOpen={isMenuOpen}
        >
          <div className="SelectPaymentTypeParentModal__mobileMenu flex flex-column bg-white min-h-100 pb20">
            <div className="w-100 flex justify-end pv4 mb2 bb b--lightest-gray">
              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  props.onCloseClick();
                }}
              >
                <Icon
                  name="fetch-close"
                  className="ph2 pv2 mr4 ba b--light-gray br-100"
                  height="32px"
                  width="32px"
                />
              </button>
            </div>
            <CurrentModalDisplay
              currentModalName={currentModalName}
              onChangeModal={handleChangeModal}
              onGoBack={previousModalName ? handleGoBack : false}
              onHandleSelection={handleSelection}
              selectedProtectionType={selectedProtectionType}
              onSave={handleSaveSelection}
              {...props}
              paymentMethod={paymentMethod}
            />
          </div>
        </MobileMenu>
      </div>
    );
  }

  return (
    <MiniOverlayModal
      className="SelectPaymentTypeParentModal"
      onCloseClick={props.onCloseClick}
      closeButtonClassName="padding-h-6 padding-v-6 mt4 mr4 ba b--light-gray br-100"
      closeButtonSize="20px"
      contentClassName="flex flex-column overflow-y-hidden bg-white"
      hideCloseButton={Boolean(previousModalName)}
    >
      <CurrentModalDisplay
        currentModalName={currentModalName}
        onChangeModal={handleChangeModal}
        onGoBack={previousModalName ? handleGoBack : false}
        onHandleSelection={handleSelection}
        selectedProtectionType={selectedProtectionType}
        onSave={handleSaveSelection}
        {...props}
        paymentMethod={paymentMethod}
      />
    </MiniOverlayModal>
  );
}
